import {
  Text as NativeText,
  Pressable,
  type PressableProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Text from '@/components/UI/Text';

type ChipSize = 'sm' | 'md' | 'lg';
type ChipVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'info'
  | 'error'
  | 'ghost';

interface ChipProps
  extends Omit<
    PressableProps,
    | 'accessibilityRole'
    | 'accessibilityState'
    | 'disabled'
    | 'onPress'
    | 'style'
  > {
  accessibilityRole?: PressableProps['accessibilityRole'];
  children?: React.ReactNode;
  disabled?: boolean;
  label?: string;
  labelSuffix?: string;
  onValueChange?: (value: boolean) => void;
  size?: ChipSize;
  style?: StyleProp<ViewStyle>;
  value?: boolean;
  variant?: ChipVariant;
}

export default function Chip({
  value,
  disabled = false,
  label,
  labelSuffix,
  children,
  accessibilityLabel,
  variant = 'ghost',
  size = 'md',
  style: extraStyles,
  onValueChange,
  ...props
}: ChipProps) {
  const checked = !!value;

  style.useVariants({
    selected: checked,
    disabled,
    variant,
    size,
  });

  const handlePress = () => {
    if (disabled) {
      return;
    }

    onValueChange?.(!checked);
  };

  return (
    <Pressable
      {...props}
      aria-checked={checked}
      accessibilityLabel={
        accessibilityLabel ?? (labelSuffix ? `${label} ${labelSuffix}` : label)
      }
      accessibilityState={{ checked, disabled }}
      disabled={disabled}
      onPress={handlePress}
      style={({ pressed }) => [
        style.container(pressed),
        extraStyles,
        disabled ? style.disabled : undefined,
      ]}
    >
      {children}
      {label ? (
        <Text numberOfLines={1} style={style.label}>
          {label}
          {labelSuffix ? (
            <NativeText style={style.labelSuffix}> {labelSuffix}</NativeText>
          ) : null}
        </Text>
      ) : null}
    </Pressable>
  );
}

const style = StyleSheet.create((theme) => ({
  container: (pressed: boolean) => ({
    alignItems: 'center',
    borderColor: theme.colors.borderMuted,
    borderRadius: theme.borderRadius.full,
    borderWidth: theme.size(0.5),
    flexDirection: 'row',
    gap: theme.size(2),
    justifyContent: 'center',
    backgroundColor: pressed ? theme.colors.surfaceMuted : 'transparent',
    _web: {
      _hover: {
        backgroundColor: theme.colors.surfaceMuted,
      },
    },
    variants: {
      disabled: {
        true: {},
      },
      selected: {
        true: {
          backgroundColor: theme.colors.surfaceInverted,
          _web: {
            _hover: {
              backgroundColor: theme.colors.surfaceInverted,
            },
          },
        },
        false: {
          backgroundColor: 'transparent',
          borderColor: theme.colors.borderMuted,
        },
      },
      size: {
        sm: {
          minHeight: theme.size(8),
          paddingHorizontal: theme.size(2),
          paddingVertical: theme.size(1),
        },
        md: {
          minHeight: theme.size(10),
          paddingHorizontal: theme.size(3),
          paddingVertical: theme.size(2),
        },
        lg: {
          minHeight: theme.size(12),
          paddingHorizontal: theme.size(4),
          paddingVertical: theme.size(3),
        },
      },
      variant: {
        primary: {},
        secondary: {},
        success: {},
        warning: {},
        info: {},
        error: {},
        ghost: {},
      },
    },
    compoundVariants: [
      {
        selected: true,
        variant: 'primary',
        styles: {
          backgroundColor: theme.colors.primary,
          _web: {
            _hover: {
              backgroundColor: theme.colors.primaryHover,
            },
          },
        },
      },
      {
        selected: true,
        variant: 'secondary',
        styles: {
          backgroundColor: theme.colors.secondary,
          _web: {
            _hover: {
              backgroundColor: theme.colors.secondaryHover,
            },
          },
        },
      },
      {
        selected: true,
        variant: 'success',
        styles: {
          backgroundColor: theme.colors.success,
          _web: {
            _hover: {
              backgroundColor: theme.colors.successHover,
            },
          },
        },
      },
      {
        selected: true,
        variant: 'warning',
        styles: {
          backgroundColor: theme.colors.warning,
          _web: {
            _hover: {
              backgroundColor: theme.colors.warningHover,
            },
          },
        },
      },
      {
        selected: true,
        variant: 'info',
        styles: {
          backgroundColor: theme.colors.info,
          _web: {
            _hover: {
              backgroundColor: theme.colors.infoHover,
            },
          },
        },
      },
      {
        selected: true,
        variant: 'error',
        styles: {
          backgroundColor: theme.colors.error,
          _web: {
            _hover: {
              backgroundColor: theme.colors.errorHover,
            },
          },
        },
      },
    ],
  }),
  labelSuffix: {
    fontSize: theme.fontSize.sm,
  },
  disabled: {
    opacity: 0.5,
  },
  label: {
    flexShrink: 1,
    includeFontPadding: false,
    variants: {
      selected: {
        true: {
          color: theme.colors.typographyInverted,
        },
        false: {
          color: theme.colors.typography,
        },
      },
      disabled: {},
      size: {
        sm: {
          fontSize: theme.fontSize.sm,
          lineHeight: theme.fontSize.sm + theme.size(1),
        },
        md: {
          fontSize: theme.fontSize.md,
          lineHeight: theme.fontSize.md + theme.size(1),
        },
        lg: {
          fontSize: theme.fontSize.lg,
          lineHeight: theme.fontSize.lg + theme.size(1),
        },
      },
      variant: {},
    },
  },
}));
